// Schema-driven object editor.  Splits across multiple files to keep the
// Emergent visual-edits Babel plugin from running out of stack on deeply
// nested JSX.

import React from "react";
import IconSelect from "./IconSelect";
import StringListEdit from "./StringListEdit";
import UploadPicker from "./UploadPicker";
import ListEdit from "./ListEdit";
import { CTA_KIND_OPTIONS } from "./schemas";

const inputBase =
    "w-full border border-slate-300 px-3 py-2 text-[13.5px] focus:outline-none focus:border-teal-600";

const labelFor = (key, field) =>
    field?.label || key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export function makeBlank(schema) {
    const out = {};
    for (const [key, field] of Object.entries(schema || {})) {
        if (field.type === "bool") out[key] = field.default ?? true;
        else if (field.type === "list" || field.type === "string_list") out[key] = [];
        else if (field.type === "object") out[key] = makeBlank(field.fields || {});
        else out[key] = "";
    }
    return out;
}

function renderInput(field, value, setValue) {
    const t = field.type;
    if (t === "string") {
        return (
            <input
                type="text"
                className={inputBase}
                value={value ?? ""}
                onChange={(e) => setValue(e.target.value)}
            />
        );
    }
    if (t === "textarea") {
        return (
            <textarea
                rows={4}
                className={inputBase + " font-mono"}
                value={value ?? ""}
                onChange={(e) => setValue(e.target.value)}
            />
        );
    }
    if (t === "icon") return <IconSelect value={value} onChange={setValue} />;
    if (t === "cta_kind") {
        return (
            <select
                className={inputBase}
                value={value || "external"}
                onChange={(e) => setValue(e.target.value)}
            >
                {CTA_KIND_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                ))}
            </select>
        );
    }
    if (t === "string_list") return <StringListEdit value={value} onChange={setValue} />;
    if (t === "image") return <UploadPicker value={value} onChange={setValue} accept="image/*" />;
    if (t === "file") return <UploadPicker value={value} onChange={setValue} accept="application/pdf,image/*" />;
    return null;
}

export default function ObjectFields({ schema, value, onChange, depth = 0 }) {
    const update = (key, val) => onChange({ ...(value || {}), [key]: val });

    return (
        <div className={depth === 0 ? "space-y-5" : "space-y-4"}>
            {Object.entries(schema || {}).map(([key, field]) => {
                const labelText = labelFor(key, field);
                const fieldValue = value?.[key];
                const setValue = (v) => update(key, v);

                if (field.type === "bool") {
                    return (
                        <div key={key}>
                            <label className="inline-flex items-center gap-2 text-[13px] text-slate-700">
                                <input
                                    type="checkbox"
                                    checked={!!fieldValue}
                                    onChange={(e) => setValue(e.target.checked)}
                                    className="w-4 h-4 accent-teal-600"
                                />
                                <span>{labelText}</span>
                            </label>
                        </div>
                    );
                }

                if (field.type === "list") {
                    return (
                        <div key={key}>
                            <label className="block text-[12.5px] font-medium text-slate-700 mb-1.5">{labelText}</label>
                            <ListEdit field={field} value={fieldValue} onChange={setValue} depth={depth} />
                        </div>
                    );
                }

                if (field.type === "object") {
                    return (
                        <div key={key}>
                            <label className="block text-[12.5px] font-medium text-slate-700 mb-1.5">{labelText}</label>
                            <div className="border-l-2 border-slate-200 pl-4">
                                <ObjectFields
                                    schema={field.fields}
                                    value={fieldValue || {}}
                                    onChange={setValue}
                                    depth={depth + 1}
                                />
                            </div>
                        </div>
                    );
                }

                const input = renderInput(field, fieldValue, setValue);
                return (
                    <div key={key}>
                        <label className="block text-[12.5px] font-medium text-slate-700 mb-1.5">{labelText}</label>
                        {input}
                    </div>
                );
            })}
        </div>
    );
}
